import { UploadOutlined } from '@ant-design/icons';
import { Alert, Button, Typography } from 'antd';
import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import api from 'common/api';
import { ClientIdentityDocumentMedia, isClientIdentityDocumentFileTooLarge } from 'common/api/media';
import { I18nNameSpaces } from 'common/services/i18n';
import useMount from 'common/hooks/useMount';
import dayjs from 'common/services/dayjs';
import { message } from 'common/services/message';

const ACCEPTED_FILE_TYPES = 'application/pdf,image/jpeg,image/png';

// Temporarily hidden.
const isUploadButtonTemporarilyHidden = true;

type Props = {
  transactionId: string;
  disabled?: boolean;
};

function isUploadFileTooLargeApiError(response: { status?: number; data?: unknown }): boolean {
  if (response.status === 413) {
    return true;
  }

  if (response.status !== 400 || typeof response.data !== 'object' || response.data === null) {
    return false;
  }

  const messageText = 'message' in response.data ? String(response.data.message) : '';

  return messageText.toLowerCase().includes('too big');
}

function getUploadErrorMessage(
  translate: (key: string) => string,
  response: { status?: number; data?: unknown },
): string {
  if (isUploadFileTooLargeApiError(response)) {
    return translate('informationsFeature.clientIdentityDocument.uploadTooLarge');
  }

  return translate('informationsFeature.clientIdentityDocument.uploadFailed');
}

function pickLatestDocument(documents: ClientIdentityDocumentMedia[]): ClientIdentityDocumentMedia | undefined {
  return [...documents].sort((left, right) => dayjs(right.createdAt).valueOf() - dayjs(left.createdAt).valueOf())[0];
}

export default function ClientIdentityDocumentUpload({ transactionId, disabled }: Props) {
  const { t } = useTranslation(I18nNameSpaces.Common);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [latestDocument, setLatestDocument] = useState<ClientIdentityDocumentMedia | undefined>();
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  async function loadDocuments() {
    setIsLoadingList(true);

    try {
      const response = await api.media.listClientIdentityDocuments(transactionId);

      if (response.ok) {
        setLatestDocument(pickLatestDocument(response.data));
      }
    } finally {
      setIsLoadingList(false);
    }
  }

  useMount(() => {
    void loadDocuments();
  });

  function handleUploadClick() {
    fileInputRef.current?.click();
  }

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    const allowedMimeTypes = new Set(['application/pdf', 'image/jpeg', 'image/png']);

    if (!allowedMimeTypes.has(file.type)) {
      message.error(t('informationsFeature.clientIdentityDocument.uploadInvalidType'));
      return;
    }

    if (isClientIdentityDocumentFileTooLarge(file.size)) {
      message.error(t('informationsFeature.clientIdentityDocument.uploadTooLarge'));
      return;
    }

    setIsUploading(true);

    try {
      const response = await api.media.uploadClientIdentityDocument(transactionId, file);

      if (response.ok) {
        setLatestDocument(response.data);
        message.success(t('informationsFeature.clientIdentityDocument.uploadSuccess'));
        return;
      }

      message.error(getUploadErrorMessage(t, response));
    } catch {
      message.error(t('informationsFeature.clientIdentityDocument.uploadFailed'));
    } finally {
      setIsUploading(false);
    }
  }

  if (isUploadButtonTemporarilyHidden && !latestDocument) {
    return null;
  }

  return (
    <div className="client-identity-document-upload">
      {isUploadButtonTemporarilyHidden ? null : (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            className="client-identity-document-upload__input"
            disabled={disabled || isUploading}
            onChange={handleFileChange}
          />
          <Button
            icon={<UploadOutlined />}
            loading={isUploading}
            disabled={disabled || isLoadingList}
            onClick={handleUploadClick}
          >
            {t('informationsFeature.clientIdentityDocument.uploadButton')}
          </Button>
          <Typography.Text type="secondary" className="client-identity-document-upload__hint">
            {t('informationsFeature.clientIdentityDocument.uploadHint')}
          </Typography.Text>
        </>
      )}
      {latestDocument ? (
        <Alert
          className="client-identity-document-upload__status"
          type="success"
          showIcon
          title={t('informationsFeature.clientIdentityDocument.uploaded')}
          description={t('informationsFeature.clientIdentityDocument.uploadedAt', {
            date: dayjs(latestDocument.createdAt).format('L LT'),
          })}
        />
      ) : null}
    </div>
  );
}
